/**
 * Avaliador de regras dos campos PADRÃO (modo Cadastro Customizado).
 *
 * Porte VERBATIM de verso-apps/packages/commons/src/helpers/regrasCampoCadastro.js
 * (espelho EXATO do backend CadastroCampoConfigService::avaliar). Usado no PREVIEW do
 * admin para simular gatilhos e mostrar visível/obrigatório resultante. NÃO alterar a
 * lógica sem alterar o backend (paridade).
 */

export function derivarTipoPessoa(valores = {}) {
  const documento = valores.documento ?? valores.cnpj ?? valores.cpf ?? null;
  if (documento == null) return null;
  const digitos = String(documento).replace(/\D/g, '');
  if (digitos.length === 11) return 'PF';
  if (digitos.length === 14) return 'PJ';
  return null;
}

function normalizar(chave, valor) {
  if (valor == null) return null;
  if (
    chave === 'documento' ||
    chave === 'cnae' ||
    chave === 'cnae_qualquer' ||
    chave === 'cnae_principal' ||
    chave === 'cnaes_secundario'
  ) {
    return String(valor).replace(/\D/g, '');
  }
  if (chave === 'situacao_cadastral') {
    return String(valor).trim().toUpperCase();
  }
  return String(valor);
}

function comparaEscalar(operador, valor, alvo, chave) {
  switch (operador) {
    case 'igual':
      return valor != null && valor === normalizar(chave, alvo);
    case 'em': {
      const lista = (Array.isArray(alvo) ? alvo : [alvo]).map((v) => normalizar(chave, v));
      return valor != null && lista.includes(valor);
    }
    case 'comeca_com':
      return valor != null && alvo != null && valor.startsWith(normalizar(chave, alvo) ?? '');
    case 'preenchido':
      return valor != null && valor !== '';
    default:
      return false;
  }
}

function condicaoCasa(condicao, valores) {
  const chave = condicao.chave;
  const operador = condicao.operador;
  const alvo = condicao.valor ?? null;
  const fonte = condicao.fonte || 'campo';
  const origem = fonte === 'receita' ? valores.receita || {} : valores;

  let bruto;
  if (chave === 'cnae_qualquer') {
    bruto = [origem.cnae ?? origem.cnae_principal ?? null, ...(origem.cnaes_secundario || [])];
  } else {
    bruto = origem[chave] ?? null;
  }

  if (Array.isArray(bruto)) {
    const itens = bruto.map((v) => normalizar(chave, v)).filter((v) => v != null && v !== '');
    return itens.some((item) => comparaEscalar(operador, item, alvo, chave));
  }
  return comparaEscalar(operador, normalizar(chave, bruto), alvo, chave);
}

function regraCasa(regra, valores) {
  const condicoes = regra.condicoes || [];
  if (!condicoes.length) return false;
  const conector = String(regra.conector || 'E').toUpperCase();
  for (const condicao of condicoes) {
    const casa = condicaoCasa(condicao, valores);
    if (conector === 'OU' && casa) return true;
    if (conector === 'E' && !casa) return false;
  }
  return conector === 'E';
}

export function avaliarCampo(campo, valores) {
  let visivel = campo.visivel != null ? !!campo.visivel : true;
  let obrigatorio = !!campo.obrigatorio;
  let label = campo.label != null ? campo.label : null; // sobrescrito por `rotular` que casar

  let temOculto = false;
  let temVisivel = false;
  let temObrigatorio = false;
  let temOpcional = false;
  let temDesabilitar = false;

  (campo.regras || []).forEach((regra) => {
    if (!regraCasa(regra, valores)) return;
    switch (regra.efeito) {
      case 'oculto': temOculto = true; break;
      case 'visivel': temVisivel = true; break;
      case 'obrigatorio': temObrigatorio = true; break;
      case 'opcional': temOpcional = true; break;
      case 'desabilitar': temDesabilitar = true; break;
      case 'rotular': if (regra.rotulo != null && regra.rotulo !== '') label = regra.rotulo; break; // última que casa vence (paridade PHP: !== '')
      default: break;
    }
  });

  if (temOculto) visivel = false;
  else if (temVisivel) visivel = true;

  // `desabilitar`: visível porém travado, tratado como vazio no submit; nunca obrigatório.
  const desabilitado = visivel && temDesabilitar;

  if (!visivel || temDesabilitar) obrigatorio = false;
  else if (temObrigatorio) obrigatorio = true;
  else if (temOpcional) obrigatorio = false;

  // Piso estrutural: IMUTÁVEL — regras de afrouxamento (oculto/opcional/desabilitar) não
  // derrubam o piso de integração; só `rotular` (label) vale. Espelha o clamp da fonte PHP.
  if (campo.estrutural) return { visivel: true, obrigatorio: true, label, desabilitado: false };

  return { visivel, obrigatorio, label, desabilitado };
}

export function avaliarCampos(campos, valores = {}) {
  const vals = { ...valores, tipo_pessoa: valores.tipo_pessoa ?? derivarTipoPessoa(valores) };
  const resultado = {};
  (campos || []).forEach((campo) => {
    resultado[campo.campo] = avaliarCampo(campo, vals);
  });
  // vinculados (ex.: isento ↔ IE): a visibilidade segue o alvo (espelho do backend).
  (campos || []).forEach((campo) => {
    const alvo = campo.vinculadoA;
    if (alvo && resultado[alvo] && !resultado[alvo].visivel) {
      resultado[campo.campo] = { ...resultado[campo.campo], visivel: false, obrigatorio: false, desabilitado: false };
    }
  });
  return resultado;
}

function mensagemBloqueio(regra) {
  for (const c of regra.condicoes || []) {
    if ((c.fonte || 'campo') === 'receita') {
      return 'Cadastro não permitido: situação cadastral irregular na Receita Federal.';
    }
    if (['cnae', 'cnae_qualquer', 'cnae_principal', 'cnaes_secundario'].includes(c.chave)) {
      return 'Cadastro não permitido para a atividade econômica (CNAE) informada.';
    }
  }
  return 'Cadastro não permitido pelas regras configuradas.';
}

export function avaliarBloqueios(campos, valores = {}) {
  const vals = { ...valores, tipo_pessoa: valores.tipo_pessoa ?? derivarTipoPessoa(valores) };
  const mensagens = [];
  (campos || []).forEach((campo) => {
    (campo.regras || []).forEach((regra) => {
      if (regra.efeito === 'bloqueia_cadastro' && regraCasa(regra, vals)) {
        mensagens.push(mensagemBloqueio(regra));
      }
    });
  });
  return { bloqueado: mensagens.length > 0, mensagens: [...new Set(mensagens)] };
}

/**
 * Avalia as regras das SEÇÕES (efeito `rotular`) — renomeia o nome da seção por condição.
 * Espelho de CadastroCampoConfigService::avaliarSecoes. Retorna { [secao]: { label } }.
 */
export function avaliarSecoes(secoes, valores = {}) {
  const vals = { ...valores, tipo_pessoa: valores.tipo_pessoa ?? derivarTipoPessoa(valores) };
  const resultado = {};
  (secoes || []).forEach((secao) => {
    let label = secao.label != null ? secao.label : secao.secao;
    (secao.regras || []).forEach((regra) => {
      if (regra.efeito === 'rotular' && regra.rotulo != null && regra.rotulo !== '' && regraCasa(regra, vals)) {
        label = regra.rotulo; // última que casa vence (paridade PHP: !== '')
      }
    });
    resultado[secao.secao] = { label };
  });
  return resultado;
}
